const CONNECTION_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.:@+~-]{0,159}$/;
const CONNECTION_STATES = new Set([
  "draft",
  "pending_authorization",
  "connected",
  "degraded",
  "reauthorization_required",
]);
const HEALTH_STATES = new Set([
  "unknown",
  "healthy",
  "degraded",
  "action_required",
]);

const clean = (value) => String(value ?? "").trim();

/**
 * Treat URL and API connection identifiers as untrusted presentation input.
 * The backend remains authoritative, but rejecting malformed values here
 * avoids issuing ambiguous requests or keeping unsafe state in browser URLs.
 */
export function safeConnectionId(value) {
  const normalized = clean(value);
  return CONNECTION_ID_PATTERN.test(normalized) ? normalized : "";
}

/** Normalize the shared backend source-context envelope for presentation. */
export function normalizeSourceContext(meta) {
  const source = meta?.source_context;
  if (!source || !["all_permitted", "single"].includes(source.mode)) return null;

  const seen = new Set();
  const options = (Array.isArray(source.options) ? source.options : [])
    .map((option) => {
      const state = clean(option?.state).toLowerCase();
      const healthStatus = clean(
        option?.health_status || option?.health?.status
      ).toLowerCase();
      const explicitReadiness = typeof option?.provider_ready === "boolean"
        ? option.provider_ready
        : null;

      return {
        // Transitional aliases let older account endpoints converge without
        // weakening the server-authorized option contract.
        id: safeConnectionId(option?.id || option?.connection_id),
        display_name: clean(option?.display_name),
        provider: clean(option?.provider),
        mailbox_address: clean(option?.mailbox_address || option?.mailbox_email),
        // Presentation metadata only. Consumers still validate the IANA name
        // before using it, and the selected connection remains server-derived.
        timezone_name: clean(option?.timezone_name),
        lifecycle_state: CONNECTION_STATES.has(state) ? state : "",
        health_status: HEALTH_STATES.has(healthStatus) ? healthStatus : "",
        // `null` means an older response did not publish readiness. It must
        // not be converted to false and incorrectly labelled as disconnected.
        provider_ready: explicitReadiness,
      };
    })
    .filter((option) => {
      if (!option.id || seen.has(option.id)) return false;
      seen.add(option.id);
      return true;
    });

  const selectedConnectionId = safeConnectionId(source.selected_connection_id);
  const appliesTo = Array.isArray(source.applies_to)
    ? source.applies_to.map(clean).filter(Boolean)
    : [];

  return {
    mode: source.mode,
    selected_connection_id: selectedConnectionId || null,
    options,
    applies_to: appliesTo,
  };
}
